import { useWorkspaceTree } from "@/api/fileRecords";
import type { FilesRailFileMenuActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import type { WorkspaceDropPayload } from "@/pages/workspaces/components/WorkspaceDropZone";
import { useFileRecordCardActions } from "@/pages/workspaces/hooks/useFileRecordCardActions";
import { useWorkspaceUploadPickers } from "@/pages/workspaces/hooks/useWorkspaceUploadPickers";
import { useCallback, useMemo } from "react";

export type FilesRailOsDropHandler = (payload: WorkspaceDropPayload) => void;
export type FilesRailOsDropAtPath = (
  parentPath: string,
) => FilesRailOsDropHandler;

export function useFilesRailWorkspaceFiles(
  workspaceId: string | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!workspaceId;

  // The workspace tree: folders, the canvases filed in them, and files.
  const {
    data: fileRecords,
    isPending,
    isError,
  } = useWorkspaceTree(workspaceId, { enabled });

  const { uploadQueue, uploadInputs, uploadFilesTo, uploadFolderTo } =
    useWorkspaceUploadPickers(workspaceId);

  const { dialogs, actions } = useFileRecordCardActions({
    workspaceId: workspaceId ?? "",
    allRecords: fileRecords ?? [],
    onFolderOpen: () => {},
  });

  const menuActions = useMemo((): FilesRailFileMenuActions | undefined => {
    if (!workspaceId) return undefined;
    return {
      onRename: actions.rename,
      onCopy: actions.copy,
      onDelete: actions.delete,
    };
  }, [workspaceId, actions.rename, actions.copy, actions.delete]);

  const handleUploadClick = useCallback(
    () => uploadFilesTo(""),
    [uploadFilesTo],
  );
  const handleUploadFolderClick = useCallback(
    () => uploadFolderTo(""),
    [uploadFolderTo],
  );

  const handleOsDropAtPath = useCallback(
    (parentPath: string, payload: WorkspaceDropPayload) => {
      if (!workspaceId) return;
      if (payload.tree) {
        uploadQueue.enqueueTree(payload.tree, parentPath);
        return;
      }
      if (payload.files.length > 0) {
        uploadQueue.enqueueFiles(payload.files, parentPath);
      }
    },
    [uploadQueue, workspaceId],
  );

  const getOsDropHandler = useCallback(
    (parentPath: string): FilesRailOsDropHandler =>
      (payload) =>
        handleOsDropAtPath(parentPath, payload),
    [handleOsDropAtPath],
  );

  return {
    fileRecords,
    isPending,
    isError,
    uploadQueue,
    uploadInputs,
    dialogs,
    actions,
    menuActions,
    handleUploadClick,
    handleUploadToPath: uploadFilesTo,
    handleUploadFolderClick,
    handleUploadFolderToPath: uploadFolderTo,
    handleOsDropAtPath,
    getOsDropHandler,
  };
}
